// client/src/pages/secure/TeamDetailPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useApiClient } from '../../hooks/useApiClient';
import { getCurrentUser, canManageStaff } from '../../hooks/useCurrentUser';

const TeamDetailPage = () => {
    const { id: teamId } = useParams();
    const [team, setTeam] = useState(null);
    const [availableAgents, setAvailableAgents] = useState([]);
    const [selectedAgent, setSelectedAgent] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const apiClient = useApiClient();

    // --- NEW CODE START ---
    // State to manage edit mode and form data
    const [isEditing, setIsEditing] = useState(false);
    const [editFormData, setEditFormData] = useState({ team_name: '', description: '' });
    // --- NEW CODE END ---

    const fetchTeamData = useCallback(async () => {
        setIsLoading(true);
        setError(null); // Clear previous errors on refetch
        try {
            const [teamData, availableAgentsData] = await Promise.all([
                apiClient.get(`/api/teams/${teamId}`),
                apiClient.get(`/api/teams/${teamId}/available-agents`)
            ]);
            setTeam(teamData);
            // --- MODIFIED CODE START ---
            // Pre-fill edit form data once team data is loaded
            setEditFormData({ team_name: teamData.team_name, description: teamData.description || '' });
            // --- MODIFIED CODE END ---
            setAvailableAgents(availableAgentsData);
            if (availableAgentsData.length > 0) {
                setSelectedAgent(availableAgentsData[0].agent_id);
            } else {
                setSelectedAgent('');
            }
        } catch (err) {
            setError(err.message || 'Failed to fetch team data.');
        } finally {
            setIsLoading(false);
        }
    }, [teamId, apiClient]);

    useEffect(() => {
        fetchTeamData();
    }, [fetchTeamData]);

    const handleAddMember = async (e) => {
        e.preventDefault();
        if (!selectedAgent) {
            alert('Please select an agent to add.');
            return;
        }
        try {
            await apiClient.post(`/api/teams/${teamId}/members`, { agent_id: selectedAgent });
            fetchTeamData();
        } catch (err) {
            alert(err.message || 'Failed to add agent to the team.');
        }
    };

    const handleRemoveMember = async (agentId) => {
        if (window.confirm('Are you sure you want to remove this member from the team?')) {
            try {
                await apiClient.delete(`/api/teams/${teamId}/members/${agentId}`);
                fetchTeamData();
            } catch (err) {
                alert(err.message || 'Failed to remove agent from the team.');
            }
        }
    };

    const handleToggleLead = async (agentId, currentlyLead) => {
        try {
            await apiClient.put(`/api/teams/${teamId}/members/${agentId}/lead`, { is_lead: !currentlyLead });
            fetchTeamData();
        } catch (err) {
            alert(err.message || 'Failed to update team lead status.');
        }
    };
    
    // --- NEW CODE START ---
    // Handler for changes in the edit form
    const handleEditFormChange = (e) => {
        setEditFormData({
            ...editFormData,
            [e.target.name]: e.target.value,
        });
    };

    // Handler for submitting the updated team details
    const handleUpdateTeam = async (e) => {
        e.preventDefault();
        setError(null);
        try {
            const updatedTeam = await apiClient.put(`/api/teams/${teamId}`, editFormData);
            setTeam(updatedTeam); // Update state with the returned updated team
            setIsEditing(false); // Exit edit mode
        } catch (err) {
            setError(err.message || "Failed to update team.");
        }
    };
    // --- NEW CODE END ---


    if (isLoading) return <div className="p-8">Loading team details...</div>;
    if (error) return <div className="p-8 text-red-500">{error}</div>;
    if (!team) return <div className="p-8">Team not found.</div>;

    return (
        <div>
            <div className="mb-6">
                <Link to="/dashboard/teams" className="text-indigo-600 hover:text-indigo-900">&larr; Back to Team List</Link>
            </div>

            <div className="bg-white p-8 rounded-lg shadow">
                {/* --- MODIFIED CODE START: Conditional rendering for edit mode --- */}
                {!isEditing ? (
                    <div className="flex justify-between items-start mb-8">
                        <div>
                            <h1 className="text-3xl font-bold text-slate-800">{team.team_name}</h1>
                            <p className="text-slate-600 mt-2">{team.description}</p>
                        </div>
                        {canManageStaff(getCurrentUser()) && (
                            <button onClick={() => setIsEditing(true)} className="px-4 py-2 text-sm font-medium text-white bg-slate-800 rounded-md hover:bg-slate-900">
                                Edit Team
                            </button>
                        )}
                    </div>
                ) : (
                    <form onSubmit={handleUpdateTeam} className="mb-8">
                        <h2 className="text-2xl font-bold text-slate-800 mb-4">Edit Team Details</h2>
                        <div className="space-y-4">
                            <div>
                                <label htmlFor="team_name" className="block text-sm font-medium text-slate-700">Team Name</label>
                                <input
                                    type="text"
                                    id="team_name"
                                    name="team_name"
                                    value={editFormData.team_name}
                                    onChange={handleEditFormChange}
                                    className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                                    required
                                />
                            </div>
                            <div>
                                <label htmlFor="description" className="block text-sm font-medium text-slate-700">Description</label>
                                <textarea
                                    id="description"
                                    name="description"
                                    value={editFormData.description}
                                    onChange={handleEditFormChange}
                                    rows="3"
                                    className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                                ></textarea>
                            </div>
                        </div>
                        <div className="flex justify-end gap-4 mt-6">
                            <button type="button" onClick={() => setIsEditing(false)} className="px-4 py-2 bg-gray-200 rounded">Cancel</button>
                            <button type="submit" className="px-4 py-2 bg-slate-800 text-white rounded hover:bg-slate-900">Save Changes</button>
                        </div>
                    </form>
                )}
                {/* --- MODIFIED CODE END --- */}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Current Members Section (No changes) */}
                    <div>
                        <h2 className="text-2xl font-bold text-slate-800 mb-4">Current Members ({team.members.length})</h2>
                        <ul className="divide-y divide-gray-200">
                            {team.members.map(member => (
                                <li key={member.agent_id} className="py-3 flex justify-between items-center">
                                    <span>
                                        {member.first_name} {member.last_name}
                                        {!!member.is_lead && <span className="ml-2 px-2 py-0.5 text-xs font-semibold bg-indigo-100 text-indigo-800 rounded-full">Team Lead</span>}
                                    </span>
                                    {canManageStaff(getCurrentUser()) && (
                                        <span className="flex items-center gap-3">
                                            <button onClick={() => handleToggleLead(member.agent_id, member.is_lead)} className="text-indigo-600 hover:text-indigo-800 text-sm">
                                                {member.is_lead ? 'Remove Lead' : 'Make Lead'}
                                            </button>
                                            <button onClick={() => handleRemoveMember(member.agent_id)} className="text-red-500 hover:text-red-700 text-sm">Remove</button>
                                        </span>
                                    )}
                                </li>
                            ))}
                            {team.members.length === 0 && <p className="text-slate-500">No members on this team yet.</p>}
                        </ul>
                    </div>

                    {/* Add New Member Section */}
                    {canManageStaff(getCurrentUser()) && (
                        <div>
                            <h2 className="text-2xl font-bold text-slate-800 mb-4">Add Member</h2>
                            <form onSubmit={handleAddMember} className="bg-slate-50 p-6 rounded-lg">
                                <label className="block text-sm font-medium text-slate-700">Select an Agent</label>
                                <select
                                    value={selectedAgent}
                                    onChange={(e) => setSelectedAgent(e.target.value)}
                                    className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                                    disabled={availableAgents.length === 0}
                                >
                                    {availableAgents.map(agent => (
                                        <option key={agent.agent_id} value={agent.agent_id}>
                                            {agent.first_name} {agent.last_name}
                                        </option>
                                    ))}
                                </select>
                                {availableAgents.length === 0 && <p className="text-slate-500 text-sm mt-2">All available agents are already on this team.</p>}
                                <button
                                    type="submit"
                                    className="mt-4 w-full px-4 py-2 text-sm font-medium text-white bg-slate-800 rounded-md hover:bg-slate-900 disabled:bg-indigo-400"
                                    disabled={availableAgents.length === 0}
                                >
                                    Add to Team
                                </button>
                            </form>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TeamDetailPage;